import { useCallback, useMemo, useState } from 'react';
import { GPaginationDirection } from '../GPagination';
import { DEFAULT_ITEMS_PER_PAGE } from '..';

interface IGPaginationProps<T> {
  data: T[];
  itemsPerPage?: number;
  totalCount?: number;
}

export const useGPagination = <T>(props: IGPaginationProps<T>) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, itemsPerPage = DEFAULT_ITEMS_PER_PAGE } = props;

  const total = typeof props.totalCount === 'number' ? props.totalCount : data.length;
  const totalPages = useMemo(() => Math.ceil(total / itemsPerPage), [total, itemsPerPage]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  }, [currentPage, data, itemsPerPage]);

  const handlePageChange = (direction: GPaginationDirection) => {
    setCurrentPage((prevPage) =>
      direction === GPaginationDirection.NEXT
        ? Math.min(prevPage + 1, totalPages)
        : Math.max(prevPage - 1, 1),
    );
  };

  const handleResetPagination = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage,
    totalPages,
    paginatedData,
    handlePageChange,
    handleResetPagination,
  };
};
